import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { WORLD_OUTLINE } from "@/constants/worldOutline";

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: 0,
    transition: { delay: 0.35 + i * 0.16, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.9 + i * 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  }),
};

const GlobeCanvas = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w = 0;
    let h = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const D2R = Math.PI / 180;
    let rot = 0;
    const project = (x, y, z, R, cx, cy) => {
      const rx = x * Math.cos(rot) - z * Math.sin(rot);
      const rz = x * Math.sin(rot) + z * Math.cos(rot);
      return [cx + rx * R, cy - y * R, rz];
    };

    const CITIES = [
      ["New Delhi", 28.61, 77.21],
      ["Mumbai", 19.08, 72.88],
      ["Bengaluru", 12.97, 77.59],
      ["London", 51.51, -0.13],
      ["Manchester", 53.48, -2.24],
      ["Frankfurt", 50.11, 8.68],
      ["Paris", 48.86, 2.35],
      ["Amsterdam", 52.37, 4.9],
      ["Milan", 45.46, 9.19],
      ["New York", 40.71, -74.01],
      ["Chicago", 41.88, -87.63],
      ["Los Angeles", 34.05, -118.24],
      ["San Francisco", 37.77, -122.42],
      ["Dubai", 25.2, 55.27],
      ["Doha", 25.29, 51.53],
      ["Riyadh", 24.71, 46.68],
      ["Singapore", 1.35, 103.82],
      ["Kuala Lumpur", 3.14, 101.69],
      ["Bangkok", 13.76, 100.5],
      ["Jakarta", -6.21, 106.85],
      ["Hong Kong", 22.32, 114.17],
    ];
    const cities = CITIES.map(([, lat, lon]) => {
      const phi = lat * D2R;
      const th = lon * D2R;
      return [Math.cos(phi) * Math.cos(th), Math.sin(phi), Math.cos(phi) * Math.sin(th)];
    });
    const routes = [[0, 3, 0], [0, 5, 0.12], [1, 6, 0.25], [2, 7, 0.35], [3, 9, 0.45], [5, 8, 0.55], [3, 4, 0.62], [9, 10, 0.7], [10, 12, 0.78], [1, 3, 0.5], [0, 9, 0.85], [11, 9, 0.4], [6, 3, 0.2], [0, 13, 0.08], [1, 13, 0.32], [13, 3, 0.58], [13, 14, 0.72], [15, 13, 0.9], [0, 16, 0.18], [2, 16, 0.42], [16, 19, 0.66], [16, 20, 0.82], [0, 18, 0.52], [18, 16, 0.28]];

    const landRings = WORLD_OUTLINE.map((ring) =>
      ring.map(([lon, lat]) => {
        const phi = lat * D2R;
        const th = lon * D2R;
        return [Math.cos(phi) * Math.cos(th), Math.sin(phi), Math.cos(phi) * Math.sin(th)];
      }),
    );

    const strokePath = (pts, R, cx, cy, alpha) => {
      ctx.beginPath();
      let started = false;
      let prevX = 0;
      let prevY = 0;
      for (const [x, y, z] of pts) {
        const [px, py, rz] = project(x, y, z, R, cx, cy);
        const jump = started && Math.hypot(px - prevX, py - prevY) > R * 0.5;
        if (rz > -0.02 && !jump) {
          if (started) ctx.lineTo(px, py);
          else ctx.moveTo(px, py);
          started = true;
          prevX = px;
          prevY = py;
        } else {
          started = false;
        }
      }
      ctx.strokeStyle = `rgba(212,175,55,${alpha})`;
      ctx.lineWidth = 1;
      ctx.stroke();
    };

    const strokeLand = (R, cx, cy) => {
      for (const ring of landRings) {
        ctx.beginPath();
        let started = false;
        let prevX = 0;
        let prevY = 0;
        let culled = false;
        for (const [x, y, z] of ring) {
          const [px, py, rz] = project(x, y, z, R, cx, cy);
          const jump = started && Math.hypot(px - prevX, py - prevY) > R * 0.35;
          if (rz > 0.02 && !jump) {
            if (started) ctx.lineTo(px, py);
            else ctx.moveTo(px, py);
            started = true;
            prevX = px;
            prevY = py;
          } else {
            started = false;
            culled = true;
          }
        }
        if (!culled) {
          ctx.fillStyle = "rgba(212,175,55,0.09)";
          ctx.fill();
        }
        ctx.strokeStyle = "rgba(255,229,153,0.5)";
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    };

    const draw = (time) => {
      ctx.clearRect(0, 0, w, h);
      const cx = w / 2;
      const cy = h / 2;
      const R = Math.min(w, h) * 0.38;
      rot += 0.0022;

      const grad = ctx.createRadialGradient(cx - R * 0.25, cy - R * 0.3, R * 0.1, cx, cy, R);
      grad.addColorStop(0, "rgba(30,58,95,0.6)");
      grad.addColorStop(0.6, "rgba(15,42,74,0.35)");
      grad.addColorStop(1, "rgba(212,175,55,0.08)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "rgba(230,198,135,0.5)";
      ctx.lineWidth = 1.2;
      ctx.stroke();

      for (let lat = -60; lat <= 60; lat += 30) {
        const phi = lat * D2R;
        const pts = [];
        for (let lon = 0; lon <= 360; lon += 5) {
          const th = lon * D2R;
          pts.push([Math.cos(phi) * Math.cos(th), Math.sin(phi), Math.cos(phi) * Math.sin(th)]);
        }
        strokePath(pts, R, cx, cy, 0.17);
      }
      for (let lon = 0; lon < 360; lon += 30) {
        const th = lon * D2R;
        const pts = [];
        for (let lat = -90; lat <= 90; lat += 5) {
          const phi = lat * D2R;
          pts.push([Math.cos(phi) * Math.cos(th), Math.sin(phi), Math.cos(phi) * Math.sin(th)]);
        }
        strokePath(pts, R, cx, cy, 0.15);
      }

      strokeLand(R, cx, cy);

      CITIES.forEach(([name], i) => {
        const [px, py, rz] = project(cities[i][0], cities[i][1], cities[i][2], R, cx, cy);
        if (rz < 0.12) return;
        const a = 0.25 + 0.65 * rz;
        const glow = ctx.createRadialGradient(px, py, 0, px, py, 9);
        glow.addColorStop(0, `rgba(255,229,153,${a})`);
        glow.addColorStop(1, "rgba(255,229,153,0)");
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(px, py, 9, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgba(255,229,153,${a})`;
        ctx.beginPath();
        ctx.arc(px, py, 2.2, 0, Math.PI * 2);
        ctx.fill();
        if (rz > 0.45) {
          ctx.font = "9px 'JetBrains Mono', monospace";
          ctx.fillStyle = `rgba(226,232,240,${Math.min(1, (rz - 0.4) * 1.4)})`;
          ctx.fillText(name.toUpperCase(), px + 9, py + 3);
        }
      });

      for (const [ai, bi, phase] of routes) {
        const [ax, ay, az] = project(cities[ai][0], cities[ai][1], cities[ai][2], R, cx, cy);
        const [bx, by, bz] = project(cities[bi][0], cities[bi][1], cities[bi][2], R, cx, cy);
        if (az < -0.05 || bz < -0.05) continue;
        const mx = (ax + bx) / 2;
        const my = (ay + by) / 2;
        const dx = mx - cx;
        const dy = my - cy;
        const dist = Math.hypot(dx, dy) || 1;
        const qx = mx + (dx / dist) * 42;
        const qy = my + (dy / dist) * 42;
        ctx.strokeStyle = "rgba(230,198,135,0.4)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.quadraticCurveTo(qx, qy, bx, by);
        ctx.stroke();
        const t = (time / 2600 + phase) % 1;
        const ix = (1 - t) * (1 - t) * ax + 2 * (1 - t) * t * qx + t * t * bx;
        const iy = (1 - t) * (1 - t) * ay + 2 * (1 - t) * t * qy + t * t * by;
        ctx.fillStyle = "rgba(255,229,153,0.95)";
        ctx.beginPath();
        ctx.arc(ix, iy, 2.2, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={ref} className="h-full w-full" data-testid="hero-globe-canvas" />;
};

const STATS = [
  ["06", "Industry sectors served"],
  ["360°", "Cross-border expertise"],
  ["01", "Partner from discovery to execution"],
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yGlobe = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const go = (e, href) => {
    e.preventDefault();
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="grain relative flex min-h-screen items-center overflow-hidden bg-gradient-to-b from-royal via-[#0F2A4A] to-royal-deep"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.08),transparent_55%)]" />

      <motion.div
        style={{ y: yGlobe, opacity: fade }}
        className="absolute inset-y-0 right-[-35%] w-[110%] opacity-40 sm:right-[-15%] sm:w-[80%] lg:right-[-6%] lg:w-[58%] lg:opacity-90"
      >
        <GlobeCanvas />
      </motion.div>

      <motion.div style={{ y: yText }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-36 lg:px-10">
        <div className="max-w-2xl">
          <div className="overflow-hidden">
            <motion.p
              variants={lineReveal}
              custom={0}
              initial="hidden"
              animate="show"
              className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold sm:text-xs"
              data-testid="hero-overline"
            >
              Rayasthaila Consultancy Pvt. Ltd.
            </motion.p>
          </div>

          <h1 className="mt-8 font-serif text-4xl font-semibold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <span className="block overflow-hidden pb-1">
              <motion.span variants={lineReveal} custom={1} initial="hidden" animate="show" className="block">
                Connecting Markets.
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span variants={lineReveal} custom={2} initial="hidden" animate="show" className="block text-gold-gradient">
                Creating Opportunities.
              </motion.span>
            </span>
          </h1>

          <motion.p
            variants={fadeUp}
            custom={0}
            initial="hidden"
            animate="show"
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="hero-subtext"
          >
            An international business and trade consultancy helping companies explore new markets, build strategic
            partnerships, and turn cross-border opportunities into practical, sustainable growth.
          </motion.p>

          <motion.div variants={fadeUp} custom={1} initial="hidden" animate="show" className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#contact"
              onClick={(e) => go(e, "#contact")}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 rounded-full bg-gold px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-royal-deep transition-colors duration-300 hover:bg-gold-pale"
            >
              Start Your Expansion
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#services"
              onClick={(e) => go(e, "#services")}
              data-testid="hero-cta-secondary"
              className="rounded-full border border-white/20 px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-gold/70 hover:text-gold"
            >
              Explore Services
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={2}
            initial="hidden"
            animate="show"
            className="mt-16 grid max-w-xl grid-cols-3 gap-6 border-t border-white/10 pt-8"
            data-testid="hero-stats"
          >
            {STATS.map(([num, label]) => (
              <div key={label}>
                <p className="font-mono text-xl font-semibold text-gold sm:text-2xl">{num}</p>
                <p className="mt-2 text-[11px] leading-snug text-slate-400 sm:text-xs">{label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <ChevronDown className="animate-bounce text-gold/70" size={20} />
      </motion.div>
    </section>
  );
};
